import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { User, BookOpen, TrendingUp, TrendingDown, AlertTriangle, Award, ArrowLeft, Calendar } from 'lucide-react';

const API_URL = window.location.origin;

const statusConfig = {
  satisfatorio: { color: 'bg-green-100 text-green-700', label: 'Satisfatório' },
  atencao: { color: 'bg-yellow-100 text-yellow-700', label: 'Atenção' },
  critico: { color: 'bg-red-100 text-red-700', label: 'Crítico' }
};

export function DetalheAluno({ studentId, year, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) {
      loadData();
    }
  }, [studentId, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/central-notas/aluno/${studentId}?year=${year}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Erro ao carregar detalhe do aluno:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!studentId) return null;

  return (
    <Dialog open={!!studentId} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="detalhe-aluno-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-600" />
            Perfil Acadêmico
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Cabeçalho do Aluno */}
            <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{data.aluno?.nome}</h2>
                    <p className="text-purple-200">
                      {data.aluno?.turma?.name || 'Sem turma'} | Matrícula: {data.aluno?.matricula || '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-purple-200">Média Geral</div>
                    <div className="text-4xl font-bold">{data.resumo?.media_geral}</div>
                    <Badge className={`mt-1 ${
                      data.resumo?.status === 'satisfatorio' ? 'bg-green-500' :
                      data.resumo?.status === 'atencao' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}>
                      {statusConfig[data.resumo?.status]?.label || 'N/A'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPIs Rápidos */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-purple-100">
                <CardContent className="pt-4">
                  <div className="text-sm text-slate-500">Total de Notas</div>
                  <div className="text-2xl font-bold text-purple-600">{data.resumo?.total_notas}</div>
                </CardContent>
              </Card>
              
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-4">
                  <div className="text-sm text-green-700 flex items-center gap-1">
                    <Award className="w-4 h-4" />
                    Melhor Disciplina
                  </div>
                  <div className="font-bold text-green-700 truncate">
                    {data.resumo?.melhor_disciplina?.nome || '-'}
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {data.resumo?.melhor_disciplina?.media || '-'}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-4">
                  <div className="text-sm text-red-700 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Precisa Atenção
                  </div>
                  <div className="font-bold text-red-700 truncate">
                    {data.resumo?.pior_disciplina?.nome || '-'}
                  </div>
                  <div className="text-lg font-bold text-red-600">
                    {data.resumo?.pior_disciplina?.media || '-'}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-purple-100">
                <CardContent className="pt-4">
                  <div className="text-sm text-slate-500 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Ano Letivo
                  </div>
                  <div className="text-2xl font-bold text-purple-600">{year}</div>
                </CardContent>
              </Card>
            </div>

            {/* Alertas */}
            {data.alertas && data.alertas.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Alertas Acadêmicos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.alertas.map((alerta, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${
                        alerta.prioridade === 'alta' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          <Badge className={alerta.prioridade === 'alta' ? 'bg-red-500' : 'bg-yellow-500'}>
                            {alerta.prioridade}
                          </Badge>
                          <span className="text-sm">{alerta.mensagem}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Evolução Trimestral */}
            {data.evolucao_trimestral && data.evolucao_trimestral.length > 0 && (
              <Card className="border-purple-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Evolução Trimestral
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.evolucao_trimestral}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="trimestre" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 10]} />
                        <Tooltip 
                          formatter={(value) => [value.toFixed(2), 'Média']}
                          contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="media" 
                          stroke="#8E52AB" 
                          strokeWidth={3}
                          dot={{ fill: '#8E52AB', strokeWidth: 2, r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance por Disciplina - Radar */}
            {data.notas_por_disciplina && data.notas_por_disciplina.length > 0 && (
              <Card className="border-purple-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    Performance por Disciplina
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Radar Chart */}
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={data.notas_por_disciplina.slice(0, 8)}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="nome" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 10]} />
                          <Radar
                            name="Média"
                            dataKey="media"
                            stroke="#8E52AB"
                            fill="#8E52AB"
                            fillOpacity={0.5}
                          />
                          <Tooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Lista de Disciplinas */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {data.notas_por_disciplina.map((disc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <div className="font-medium text-slate-700">{disc.nome}</div>
                            <div className="text-xs text-slate-500">{disc.total} avaliações</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xl font-bold ${
                              disc.status === 'satisfatorio' ? 'text-green-600' :
                              disc.status === 'atencao' ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {disc.media}
                            </span>
                            <Badge className={statusConfig[disc.status]?.color}>
                              {statusConfig[disc.status]?.label}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            Aluno não encontrado
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
